<!-- src/views/documents/DocumentView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    
    <!-- 1. NAVIGATION & HEADERS (Visible on Screen Only) -->
    <div class="no-print-zone">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <FormHeader backTo="PublishedDocuments" backLabel="Back to Documents" />
      </div>

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <div class="bg-[#0A1630] rounded-[40px] p-8 md:p-10 border-b-4 border-[#20FCF1] shadow-2xl mb-10 animate-fadeIn relative overflow-hidden text-left border-x border-t border-slate-800">
          <div class="absolute top-0 right-0 p-12 opacity-5">
            <ShieldCheckIcon class="h-32 w-32 text-white" />
          </div>
          
          <div class="relative z-10 flex flex-col xl:flex-row xl:items-center justify-between gap-8 text-left">
            <div class="space-y-2">
              <div class="flex items-center gap-3 text-[#20FCF1]">
                <DocumentTextIcon class="h-5 w-5" />
                <p class="text-[9px] font-black uppercase tracking-[0.3em]">Policy & Compliance Registry</p>
              </div>
              <h1 class="text-3xl md:text-4xl font-black text-white uppercase tracking-tight leading-none">
                {{ document?.title || 'Loading Document...' }}
              </h1>
            </div>
            
            <div class="flex flex-wrap items-center gap-3">
              <button 
                @click="exportToPdf" 
                class="bg-[#20FCF1] text-[#0A1630] px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:scale-105 transition-all flex items-center gap-2 shadow-xl"
              >
                <DocumentArrowDownIcon class="h-4 w-4" />
                <span>Export PDF</span>
              </button>

              <button @click="goBack" class="bg-white/5 border border-white/10 text-white px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-white/20 transition-all shadow-lg flex items-center gap-2">
                <ArrowUturnLeftIcon class="h-4 w-4" />
                <span>Return</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 2. MAIN DOCUMENT BODY -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 document-view-body">
      
      <!-- Loading State (Screen Only) -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-32 no-print-zone">
        <div class="w-12 h-12 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full animate-spin"></div>
        <p class="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mt-4 text-center">Decrypting Document Registry...</p>
      </div>

      <!-- Error State (Screen Only) -->
      <div v-else-if="error" class="bg-rose-50 border-2 border-rose-100 p-10 rounded-[40px] text-center text-rose-600 font-black uppercase tracking-widest text-[10px] animate-fadeIn flex flex-col items-center no-print-zone">
        <ExclamationTriangleIcon class="h-10 w-10 text-rose-500 mb-4" />
        <span>{{ error }}</span>
      </div>

      <!-- 🖥️ ON-SCREEN VIEW ONLY (Hidden during print) -->
      <div v-else-if="document" class="bg-white shadow-2xl rounded-[40px] overflow-hidden border border-slate-100 animate-fadeIn no-print-zone">
        
        <div class="p-10 md:p-16 space-y-12">
          <!-- Corporate Identification -->
          <div class="flex justify-between items-start border-b-2 border-slate-50 pb-10">
            <img v-if="clientLogoUrl" :src="clientLogoUrl" alt="Logo" class="max-h-16 w-auto object-contain" />
            <div class="text-right">
              <p v-if="document.reference" class="text-lg font-black text-[#0A1630] uppercase tracking-tighter leading-none mb-1">Ref: {{ document.reference }}</p>
              <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest">Official Compliance Record</p>
            </div>
          </div>

          <!-- Document Content (HTML from Quill) -->
          <div class="prose max-w-none ql-editor !p-0 text-left" v-html="document.content"></div>

          <!-- Authorised Sign-off (Director) -->
          <div v-if="document.directorSignature" class="mt-16 pt-10 border-t-4 border-slate-50 flex flex-col items-end text-right">
            <p class="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4">Authorised Signature</p>
            <img :src="document.directorSignature" alt="Director Seal" class="h-20 w-auto object-contain mb-4" />
            <div class="space-y-1.5 text-right">
              <p class="text-sm font-black text-[#0A1630] uppercase leading-none">{{ document.signedBy?.name || 'Director' }}</p>
              <!-- 🛡️ EMBEDDED DYNAMIC JOB TITLE -->
              <p class="text-[10px] font-black text-[#20FCF1] uppercase tracking-wider leading-none mb-1">
                {{ document.signedBy?.title || document.signedBy?.jobTitle || 'Director' }}
              </p>
              <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Date of Authorisation: {{ formatDate(document.signedAt) }}</p>
            </div>
          </div>

          <!-- Registry Footer (Rendered via v-html supporting dynamic color matching) -->
          <div class="mt-12 pt-6 border-t border-slate-100 flex justify-between items-center text-[9px] font-black text-slate-400 uppercase tracking-widest">
            <div v-if="document.footerText" v-html="document.footerText" class="prose max-w-none text-[9px] text-slate-500 ql-editor !p-0"></div>
            <p>Created: {{ formatDate(document.lastEdited) }}</p>
          </div>
        </div>

        <!-- 3. ACTION INTERFACE (Screen Only) -->
        <div class="no-print-zone bg-slate-50 p-10 md:p-16 border-t border-slate-100">
            <!-- Director Signature Logic -->
            <div v-if="!document.directorSignature && isDirector" class="bg-[#0A1630] p-10 rounded-[32px] text-white shadow-2xl flex flex-col items-center text-center space-y-6">
                 <ShieldCheckIcon class="h-10 w-10 text-[#20FCF1]" />
                 <h3 class="text-xl font-black uppercase tracking-tight text-white">Review Protocol Required</h3>
                 <button @click="signDocument" :disabled="isSigning" class="w-full max-w-xs bg-[#20FCF1] text-[#0A1630] py-5 rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl">
                    {{ isSigning ? 'Synchronising...' : 'Authorise & Publish' }}
                 </button>
            </div>

            <!-- Member Acknowledgement Logic -->
            <div v-else-if="document.directorSignature && !userStore.isAdmin && !isDirector" class="flex flex-col items-center text-center">
              <div v-if="hasUserSigned" class="bg-[#EBFDF8] p-10 rounded-[32px] border-2 border-[#20FCF1]/20 flex flex-col items-center w-full">
                <CheckCircleIcon class="h-12 w-12 text-[#20FCF1] mb-4" />
                <p class="text-sm font-black text-[#0A1630] uppercase tracking-tight text-center">Policy Acknowledged</p>
              </div>
              <div v-else class="space-y-6 flex flex-col items-center">
                <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight text-center">Member Acknowledgement</h3>
                <button @click="acknowledgeDocument" :disabled="isAcknowledging" class="bg-[#0A1630] text-white px-12 py-5 rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 transition-all shadow-2xl">
                  {{ isAcknowledging ? 'Recording...' : 'Sign & Acknowledge' }}
                </button>
              </div>
            </div>
        </div>

      </div>

      <!-- 🖨️ DEDICATED PRINT SYSTEM (Hidden on Screen, Repeats headers/footers natively on print pages) -->
      <div v-if="document" class="hidden print-only-container w-full text-left bg-white font-sans text-slate-800">
        <table class="w-full border-none">
          
          <!-- REPEATING HEADER -->
          <thead>
            <tr>
              <td>
                <div class="flex justify-between items-start border-b-2 border-slate-200 pb-6 mb-8">
                  <img v-if="clientLogoUrl" :src="clientLogoUrl" alt="Logo" class="max-h-16 w-auto object-contain" />
                  <div class="text-right">
                    <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Official Compliance Record</p>
                    <h2 class="text-lg font-black text-[#0A1630] uppercase tracking-tight leading-none mb-1">
                      {{ document.title }}
                    </h2>
                    <p v-if="document.reference" class="text-xs font-bold text-slate-700 uppercase tracking-tighter">
                      Ref: {{ document.reference }}
                    </p>
                  </div>
                </div>
              </td>
            </tr>
          </thead>

          <!-- BODY SECTION (Spans naturally over page breaks) -->
          <tbody>
            <tr>
              <td>
                <!-- Core HTML Content -->
                <div class="prose max-w-none ql-editor !p-0 text-left" v-html="document.content"></div>

                <!-- Authorised Sign-off (Director) -->
                <div v-if="document.directorSignature" class="mt-12 pt-8 border-t-2 border-slate-100 flex flex-col items-end text-right page-break-avoid">
                  <p class="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-3">Authorised Signature</p>
                  <img :src="document.directorSignature" alt="Director Seal" class="h-16 w-auto object-contain mb-3" />
                  <div class="space-y-0.5 text-right">
                    <p class="text-xs font-black text-[#0A1630] uppercase leading-none">{{ document.signedBy?.name || 'Director' }}</p>
                    <!-- 🛡️ EMBEDDED PRINT JOB TITLE -->
                    <p class="text-[9px] font-black text-slate-500 uppercase tracking-wider mb-0.5 leading-none">
                      {{ document.signedBy?.title || document.signedBy?.jobTitle || 'Director' }}
                    </p>
                    <p class="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Date of Authorisation: {{ formatDate(document.signedAt) }}</p>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>

          <!-- REPEATING FOOTER -->
          <tfoot>
            <tr>
              <td>
                <div class="pt-4 border-t border-slate-100 flex justify-between items-center text-[8px] font-black text-slate-400 uppercase tracking-widest mt-8">
                  <div v-if="document.footerText" v-html="document.footerText" class="prose max-w-none text-[8px] text-slate-500 ql-editor !p-0"></div>
                  <p>Created: {{ formatDate(document.lastEdited) }}</p>
                </div>
              </td>
            </tr>
          </tfoot>

        </table>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '@/firebaseConfig';
import { useUserStore } from '@/stores/userStore';
import { 
  ExclamationTriangleIcon, CheckCircleIcon, ShieldCheckIcon, 
  DocumentTextIcon, DocumentArrowDownIcon, ArrowUturnLeftIcon
} from '@heroicons/vue/24/outline';
import FormHeader from '@/components/FormHeader.vue';

const props = defineProps({
  documentId: { type: String, required: true }
});

const userStore = useUserStore();
const functions = getFunctions(auth.app, 'europe-west2');
const router = useRouter();

const document = ref(null);
const localClientProfile = ref(null); 
const loading = ref(true);
const error = ref(null);
const isSigning = ref(false);
const isAcknowledging = ref(false);

const clientLogoUrl = computed(() => localClientProfile.value?.clientLogoUrl || userStore.clientProfile?.clientLogoUrl || '');
const isDirector = computed(() => (userStore.userProfile?.jobTitle || '').toLowerCase().includes('director'));
const hasUserSigned = computed(() => userStore.signedDocumentIds?.includes(props.documentId) || false);

const goBack = () => router.go(-1);
const exportToPdf = () => window.print();

function formatDate(timestamp) {
  if (!timestamp) return 'N/A';
  const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const signDocument = async () => {
  if (!confirm('Authorise Policy Publication?')) return;
  isSigning.value = true;
  try {
    const managePolicy = httpsCallable(functions, 'managePolicy');
    await managePolicy({ action: 'sign', policyData: { id: props.documentId } });
    if (userStore.clientId) await fetchDocument(userStore.clientId);
  } catch (err) { 
    console.error('Sign-off error:', err); 
  } finally { 
    isSigning.value = false; 
  }
};

const acknowledgeDocument = async () => {
  isAcknowledging.value = true;
  try {
    const acknowledgeDocFunc = httpsCallable(functions, 'acknowledgeDocument');
    await acknowledgeDocFunc({ documentId: props.documentId });
    userStore.addSignedDocument(props.documentId);
  } catch (err) { 
    console.error('Acknowledgement error:', err);
  } finally { 
    isAcknowledging.value = false; 
  }
};

const fetchDocument = async (clientId) => {
  loading.value = true;
  try {
    const docRef = doc(db, 'clients', clientId, 'documents', props.documentId);
    const clientRef = doc(db, 'clients', clientId);
    
    const [docSnap, clientSnap] = await Promise.all([
      getDoc(docRef),
      getDoc(clientRef)
    ]);

    if (clientSnap.exists()) {
      localClientProfile.value = clientSnap.data();
    }

    if (docSnap.exists()) {
      document.value = { id: docSnap.id, ...docSnap.data() };
    } else { 
      error.value = "Document not found."; 
    }
  } catch (err) { 
    error.value = "Registry lookup failed."; 
  } finally { 
    loading.value = false; 
  }
};

onMounted(() => { if (userStore.clientId) fetchDocument(userStore.clientId); });
watch(() => userStore.isUserProfileLoading, (isLoading) => {
  if (!isLoading && userStore.clientId) fetchDocument(userStore.clientId);
}, { immediate: true });
</script>

<style>
/* 🛡️ MISSION-CRITICAL GLOBAL PRINT OVERRIDE */
@media print {
  html, body, #app {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  /* Target every direct child of the main layout container that is NOT the main view panel */
  #app-container > :not(main),
  .no-print-zone,
  header, footer, nav, aside,
  button {
    display: none !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: hidden !important;
  }

  /* Force display of the print-only container on page 1 */
  .print-only-container {
    display: block !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  /* Reset parent document layout wrapper on print */
  .min-h-screen,
  .document-view,
  .document-view-body,
  main {
    background: transparent !important;
    min-height: auto !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  table {
    width: 100% !important;
    border-collapse: collapse !important;
    display: table !important;
  }

  thead {
    display: table-header-group !important; 
  }

  tbody {
    display: table-row-group !important;
  }

  tfoot {
    display: table-footer-group !important; 
  }

  tr {
    display: table-row !important;
    page-break-inside: avoid !important;
  }

  td {
    display: table-cell !important;
  }

  .page-break-avoid {
    page-break-inside: avoid !important; 
  }

  @page {
    size: A4 portrait;
    margin: 1.5cm; 
  }

  .print-padding-reset {
    padding: 0 !important;
  }
}

/* SCREEN STYLES (Animations & Rich Text) */
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

.ql-editor {
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.8;
  color: #334155;
}
.ql-editor h1, .ql-editor h2, .ql-editor h3 { 
  font-weight: 900;
  text-transform: uppercase;
  color: #0A1630;
  letter-spacing: -0.025em;
  margin-bottom: 1rem;
  margin-top: 2rem;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 0.5rem;
}

/* Custom size tags styling in viewport */
.ql-editor .ql-size-extra-small {
  font-size: 0.75rem !important;
}
.ql-editor .ql-size-small {
  font-size: 0.875rem !important;
}
</style>